import { Link } from 'react-router-dom';
import { Alert, Badge, Button } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { isMtffProxyEnabled } from '../../mtff/mtffClient';
import { useMtffInstances } from './useMtff';

/**
 * MTFF Instances — Liste aller verwalteten Theme-/Plugin-Targets.
 *
 * Spec: 02_applications/07_MTFF/README.md §9.1
 *       MTFF/docs/ORBIT-INTEGRATION.md §3.1
 *
 * Liefert Routing-Sprung in den Theme-Editor pro Instance. Plugin/
 * Security/Audit-Views folgen in eigenen PRs.
 */
export function MtffInstancesPage() {
  const query = useMtffInstances();
  const enabled = isMtffProxyEnabled();
  const list = query.data?.instances ?? [];

  return (
    <div>
      <PageHeader
        title="MTFF Instances"
        subtitle={`Theme + Plugin Control-Plane · ${list.length} Instances`}
      />

      {!enabled && (
        <Alert color="warning" className="small">
          MTFF-Admin-Proxy ist serverseitig nicht konfiguriert. Setze
          {' '}<code>MTFF_ADMIN_API_URL</code> und <code>MTFF_ADMIN_API_TOKEN</code>{' '}
          in der Orbit-Server-ENV (siehe DEPLOY.md).
        </Alert>
      )}

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && list.length === 0}
        emptyMessage="Keine Instances sichtbar."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 180 }}>Instance</th>
            <th>Domains</th>
            <th style={{ width: 130 }}>Audience</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 90 }}>Theme</th>
            <th style={{ width: 160 }}>Letzter Publish</th>
            <th style={{ width: 130 }}>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {list.map((i) => (
            <tr key={i.id}>
              <td className="small">
                <code>{i.tenantId}/{i.instanceId}</code>
                <div className="text-muted">#{i.id}</div>
              </td>
              <td className="small">
                {(i.domains ?? []).map((d) => (
                  <div key={d.hostname}>
                    <code>{d.hostname}</code>
                    {d.isCanonical && <Badge color="primary" pill className="ms-1 orbit-code-badge-sm">canonical</Badge>}
                  </div>
                ))}
              </td>
              <td className="small">{i.audience ?? '—'}</td>
              <td>
                <Badge
                  color={i.status === 'active' ? 'success' : 'warning'}
                  pill
                >
                  {i.status ?? '—'}
                </Badge>
              </td>
              <td className="small">v{i.themeVersion ?? '—'}</td>
              <td>
                {i.themePublishedAt ? (
                  <RelativeTime iso={i.themePublishedAt} />
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  <Button
                    size="sm" color="primary" outline tag={Link}
                    to={`/admin/settings/mtff/instances/${i.id}/theme`}
                  >
                    Theme
                  </Button>
                  <Button
                    size="sm" color="secondary" outline tag={Link}
                    to={`/admin/settings/mtff/instances/${i.id}/css`}
                  >
                    CSS
                  </Button>
                  <Button
                    size="sm" color="info" outline tag={Link}
                    to={`/admin/settings/mtff/instances/${i.id}/page-themes`}
                  >
                    Pages
                  </Button>
                  <Button
                    size="sm" color="warning" outline tag={Link}
                    to={`/admin/settings/mtff/instances/${i.id}/plugins`}
                  >
                    Plugins
                  </Button>
                  <Button
                    size="sm" color="dark" outline tag={Link}
                    to={`/admin/settings/mtff/instances/${i.id}/sources`}
                  >
                    Sources
                  </Button>
                  <Button
                    size="sm" color="danger" outline tag={Link}
                    to={`/admin/settings/mtff/instances/${i.id}/security`}
                  >
                    Security
                  </Button>
                  <Button
                    size="sm" color="success" outline tag={Link}
                    to={`/admin/settings/mtff/instances/${i.id}/ops`}
                  >
                    Ops
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </div>
  );
}
